import { Component } from "react";
class PerformanceMonitor extends Component {
  constructor(props) {
    super(props);
    this.metrics = {
      startTime: performance.now(),
      firstContentfulPaint: null,
      domContentLoaded: null,
      windowLoaded: null,
      resourcesLoaded: 0,
      totalResources: 0,
      lazyImagesLoaded: 0,
      lazyComponentsLoaded: 0,
    };

    this.observer = null;
    this.reportInterval = null;
  }

  componentDidMount() {
    if (process.env.NODE_ENV === "development") {
      this.initPerformanceTracking();
      this.trackLazyLoading();
      this.startReporting();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.reportInterval) {
      clearInterval(this.reportInterval);
    }
  }

  initPerformanceTracking = () => {
    // Track DOM Content Loaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.metrics.domContentLoaded =
          performance.now() - this.metrics.startTime;
      });
    } else {
      this.metrics.domContentLoaded = 0; // Already loaded
    }

    // Track Window Load
    if (document.readyState !== "complete") {
      window.addEventListener("load", () => {
        this.metrics.windowLoaded = performance.now() - this.metrics.startTime;
      });
    } else {
      this.metrics.windowLoaded = 0; // Already loaded
    }

    // Track resources
    this.trackResourceLoading();

    // Track Performance Observer metrics (if available)
    if ("PerformanceObserver" in window) {
      this.trackPaintMetrics();
    }
  };

  trackResourceLoading = () => {
    const images = document.querySelectorAll("img");
    this.metrics.totalResources = images.length;

    images.forEach((img) => {
      if (img.complete) {
        this.metrics.resourcesLoaded++;
      } else {
        img.addEventListener("load", () => {
          this.metrics.resourcesLoaded++;
        });
        img.addEventListener("error", () => {
          this.metrics.resourcesLoaded++;
        });
      }
    });
  };

  trackPaintMetrics = () => {
    try {
      if (typeof window !== 'undefined' && window.PerformanceObserver) {
        const observer = new window.PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === "first-contentful-paint") {
              this.metrics.firstContentfulPaint = entry.startTime;
            }
          }
        });
        observer.observe({ entryTypes: ["paint"] });
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("PerformanceObserver not supported");
      }
    }
  };

  trackLazyLoading = () => {
    // Monitor lazy image loading
    if ("MutationObserver" in window) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Node.ELEMENT_NODE = 1
              // Track lazy images
              const lazyImages = node.querySelectorAll
                ? node.querySelectorAll('[data-lazy="true"], .lazy-image')
                : [];
              this.metrics.lazyImagesLoaded += lazyImages.length;

              // Track lazy components
              if (node.classList && node.classList.contains("lazy-component")) {
                this.metrics.lazyComponentsLoaded++;
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      this.observer = observer;
    }
  };

  startReporting = () => {
    this.reportInterval = setInterval(() => {
      this.reportMetrics();
    }, 5000); // Report every 5 seconds

    // Final report on page unload
    window.addEventListener("beforeunload", () => {
      this.reportMetrics(true);
    });
  };

  reportMetrics = (final = false) => {
    const currentTime = performance.now() - this.metrics.startTime;

    const report = {
      timestamp: new Date().toISOString(),
      pageUrl: window.location.pathname,
      metrics: {
        ...this.metrics,
        currentTime,
        memoryUsage: this.getMemoryUsage(),
        networkInfo: this.getNetworkInfo(),
      },
      optimizations: {
        lazyImagesEnabled: this.metrics.lazyImagesLoaded > 0,
        lazyComponentsEnabled: this.metrics.lazyComponentsLoaded > 0,
        resourceLoadingRatio:
          this.metrics.totalResources > 0
            ? (this.metrics.resourcesLoaded / this.metrics.totalResources) * 100
            : 100,
      },
      performance: {
        domReady: this.metrics.domContentLoaded,
        pageLoad: this.metrics.windowLoaded,
        firstPaint: this.metrics.firstContentfulPaint,
      },
    };

    // Console log for development only
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_PERFORMANCE === 'true') {
      console.group(`🚀 Performance Report ${final ? "(Final)" : ""}`);
      console.log("📊 Metrics:", report.metrics);
      console.log("⚡ Optimizations:", report.optimizations);
      console.log("🎯 Performance:", report.performance);
      console.groupEnd();
    }

    // Send to analytics (if configured)
    if (this.props.onReport) {
      this.props.onReport(report);
    }

    // Store in localStorage for debugging
    if (final) {
      localStorage.setItem(
        "botaniksan_performance_final",
        JSON.stringify(report)
      );
    }
  };

  getMemoryUsage = () => {
    if ("memory" in performance) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576), // MB
      };
    }
    return null;
  };

  getNetworkInfo = () => {
    if ("connection" in navigator) {
      return {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData,
      };
    }
    return null;
  };

  render() {
    return null; // This component doesn't render anything
  }
}

export default PerformanceMonitor;



// WEBPACK FOOTER //
// ./src/components/shared/PerformanceMonitor.js